// v2.18.1 — CourseControls : overlay flottant bas-centre pour démarrer/terminer
// une course livreur. Gère 3 états : idle | active | confirming.

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import useCoursePinger from "@/hooks/useCoursePinger";
import { getPanelState, togglePanelState } from "@/lib/panelState";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function formatHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

function parseDestFromUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const d = sp.get("dest");
    const code = sp.get("code");
    const label = sp.get("label");
    if (!d) return null;
    const parts = d.split(",").map(parseFloat);
    if (parts.length !== 2 || !Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) return null;
    return { lat: parts[0], lng: parts[1], code: code || null, label: label || null };
  } catch (_) { return null; }
}

async function getCurrentOrigin() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ lat: 5.319803, lng: -4.026111, label: "Plateau (défaut)" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        resolve({ lat: latitude, lng: longitude, label: "Position GPS", accuracy_m: accuracy });
      },
      () => resolve({ lat: 5.319803, lng: -4.026111, label: "Plateau (défaut)" }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 3000 },
    );
  });
}

export default function CourseControls() {
  const [activeCourse, setActiveCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [liveDistanceKm, setLiveDistanceKm] = useState(0);
  // v2.18.4a — État collapsé persistant
  const [collapsed, setCollapsed] = useState(() => getPanelState("course", "expanded") === "collapsed");
  const toggleCollapsed = () => setCollapsed(togglePanelState("course") === "collapsed");
  // v2.18.5 — Compteur de raccourcis appliqués sur la course active (UI uniquement).
  const [rerouteCount, setRerouteCount] = useState(0);
  useEffect(() => {
    const onReroute = () => setRerouteCount((c) => c + 1);
    window.addEventListener("civ-courier-reroute-applied", onReroute);
    return () => window.removeEventListener("civ-courier-reroute-applied", onReroute);
  }, []);
  // Reset compteur quand une nouvelle course démarre / se termine.
  useEffect(() => { if (!activeCourse) setRerouteCount(0); }, [activeCourse]);

  // Resume au mount.
  // v3.6.5 — `?sim=1` : auto-play de la course (démo livraison Boutique Awa).
  const autoSimRef = useRef(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/v3/courier/courses/active`, { withCredentials: true });
        if (r.data?.ok && r.data.active) {
          setActiveCourse(r.data.active);
          setLiveDistanceKm(r.data.active.distance_km_traveled || 0);
          return;
        }
      } catch (_) { /* ignore */ }
      try {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get("sim") === "1" && !autoSimRef.current) {
          autoSimRef.current = true;
          setTimeout(() => { try { onStartRef.current?.(); } catch (_) {} }, 1200);
        }
      } catch (_) { /* noop */ }
    })();
  }, []);

  // Chrono local.
  useEffect(() => {
    if (!activeCourse) { setElapsed(0); return undefined; }
    const startMs = new Date(activeCourse.started_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeCourse]);

  // Pinger 30s actif tant qu'il y a une course.
  useCoursePinger(activeCourse?.course_id || null, {
    enabled: !!activeCourse,
    onPing: (data) => {
      if (Number.isFinite(data?.distance_km_traveled)) setLiveDistanceKm(data.distance_km_traveled);
    },
  });

  const onStartRef = useRef(null);
  const onStart = async () => {
    setLoading(true);
    try {
      const origin = await getCurrentOrigin();      const destination = parseDestFromUrl();
      const body = destination ? { origin, destination } : { origin };
      const r = await axios.post(`${API}/v3/courier/courses/start`, body, { withCredentials: true });
      if (r.data?.ok && r.data.course) {
        setActiveCourse(r.data.course);
        setLiveDistanceKm(r.data.course.distance_km_traveled || 0);
        try { window.dispatchEvent(new CustomEvent("civ-courier-course-changed", { detail: { active: true } })); } catch (_) {}
        toast.success(r.data.resumed ? "Course en cours reprise" : "Course démarrée 🛵");
      } else {
        toast.error("Démarrage impossible.");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };
  onStartRef.current = onStart;

  const finalize = async (mode) => {
    if (!activeCourse) return;
    setLoading(true);
    try {
      const endpoint = mode === "cancel" ? "cancel" : "end";
      const r = await axios.post(
        `${API}/v3/courier/courses/${activeCourse.course_id}/${endpoint}`,
        {},
        { withCredentials: true },
      );
      if (r.data?.ok && r.data.course) {
        const c = r.data.course;
        if (mode === "cancel") {
          toast(`Course annulée. ${c.distance_km_traveled} km / ${c.duration_minutes} min.`);
        } else {
          toast.success(`Course terminée — ${c.distance_km_traveled} km en ${c.duration_minutes} min.`);
        }
        setActiveCourse(null);
        setConfirming(null);
        setLiveDistanceKm(0);
        try { window.dispatchEvent(new CustomEvent("civ-courier-course-changed", { detail: { active: false } })); } catch (_) {}
      } else {
        toast.error("Terminaison impossible.");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="course-controls" style={styles.wrap}>
      {!activeCourse && (
        <button
          type="button"
          data-testid="course-start-btn"
          onClick={onStart}
          disabled={loading}
          style={{ ...styles.startBtn, opacity: loading ? 0.6 : 1 }}
        >
          {loading ? "Démarrage…" : "▶ Démarrer la course"}
        </button>
      )}

      {activeCourse && !confirming && collapsed && (
        <button
          type="button"
          data-testid="course-active-pill"
          onClick={toggleCollapsed}
          style={S.pill}
        >
          <span style={S.liveDot} />
          <span style={{ color: "#FCA5A5", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>LIVE</span>
          <span style={{ color: "#F1F5F9", fontSize: 12 }}>⏱ {formatHMS(elapsed)}</span>
          <span style={{ color: "#FBBF24", fontSize: 12 }}>📏 {liveDistanceKm.toFixed(2)} km</span>
          {rerouteCount > 0 && (
            <span data-testid="course-reroute-count-pill" style={{ color: "#86EFAC", fontSize: 11 }}>🔁 {rerouteCount}</span>
          )}
          <span style={{ color: "#94A3B8", fontSize: 11, marginLeft: 4 }}>⌃</span>
        </button>
      )}

      {activeCourse && !confirming && !collapsed && (
        <div data-testid="course-active-panel" style={styles.panel}>
          <div style={styles.row}>
            <span style={styles.liveDot} />
            <strong style={styles.liveLabel}>LIVE</strong>
            <span style={styles.metric} data-testid="course-elapsed">
              ⏱ <strong>{formatHMS(elapsed)}</strong>
            </span>
            <span style={styles.metric} data-testid="course-distance">
              📏 <strong>{liveDistanceKm.toFixed(2)} km</strong>
            </span>
            {rerouteCount > 0 && (
              <span style={styles.metric} data-testid="course-reroute-count" title="Raccourcis appliqués sur cette course">
                🔁 <strong>{rerouteCount}</strong>
              </span>
            )}
            <button
              type="button"
              data-testid="course-collapse-btn"
              onClick={toggleCollapsed}
              style={S.chevronBtn}
              title="Réduire"
            >⌄</button>
          </div>
          <div style={styles.row2}>
            <button
              type="button"
              data-testid="course-cancel-btn"
              onClick={() => setConfirming("cancel")}
              style={styles.cancelBtn}
              disabled={loading}
            >Annuler</button>
            <button
              type="button"
              data-testid="course-end-btn"
              onClick={() => setConfirming("end")}
              style={styles.endBtn}
              disabled={loading}
            >⏹ Terminer</button>
          </div>
        </div>
      )}

      {activeCourse && confirming && (
        <div data-testid="course-confirm-modal" style={styles.confirmCard}>
          <div style={styles.confirmTitle}>
            {confirming === "cancel" ? "Annuler cette course ?" : "Terminer cette course ?"}
          </div>
          <div style={styles.confirmStats}>
            Distance : <strong>{liveDistanceKm.toFixed(2)} km</strong> · Durée : <strong>{formatHMS(elapsed)}</strong>
          </div>
          <div style={styles.confirmBtns}>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              style={styles.confirmNo}
              disabled={loading}
            >Non</button>
            <button
              type="button"
              data-testid="course-confirm-yes"
              onClick={() => finalize(confirming)}
              style={confirming === "cancel" ? styles.confirmYesCancel : styles.confirmYesEnd}
              disabled={loading}
            >{loading ? "…" : (confirming === "cancel" ? "Confirmer l'annulation" : "Oui, terminer")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    position: "fixed",
    left: "50%",
    bottom: 192,
    transform: "translateX(-50%)",
    zIndex: 50,
    width: "min(94vw, 320px)",
    pointerEvents: "auto",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  startBtn: {
    width: "100%",
    padding: "14px 18px",
    background: "linear-gradient(135deg, #16A34A, #22C55E)",
    color: "#0F172A",
    border: "none",
    borderRadius: 14,
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: "0.02em",
    boxShadow: "0 10px 28px rgba(34, 197, 94, 0.35), 0 0 0 1px rgba(255,255,255,0.12)",
    cursor: "pointer",
  },
  panel: {
    background: "rgba(15, 23, 42, 0.92)",
    border: "1px solid rgba(245, 158, 11, 0.45)",
    borderRadius: 14,
    padding: "12px 14px",
    color: "#F1F5F9",
    backdropFilter: "blur(14px)",
    boxShadow: "0 14px 36px rgba(0,0,0,0.5)",
  },
  row: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  row2: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 },
  liveDot: {
    width: 9, height: 9, borderRadius: "50%", background: "#EF4444",
    boxShadow: "0 0 8px rgba(239,68,68,0.9)",
    animation: "course-pulse 1.2s ease-in-out infinite",
  },
  liveLabel: { color: "#FCA5A5", fontSize: 11, letterSpacing: "0.1em", fontWeight: 700 },
  metric: { fontSize: 13, color: "#E2E8F0", display: "inline-flex", gap: 4, alignItems: "center" },
  cancelBtn: {
    background: "transparent",
    color: "#CBD5E1",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 999,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  endBtn: {
    background: "linear-gradient(135deg, #DC2626, #EF4444)",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(239,68,68,0.4)",
  },
  confirmCard: {
    background: "rgba(15, 23, 42, 0.97)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 14,
    padding: 16,
    color: "#F1F5F9",
    boxShadow: "0 22px 50px rgba(0,0,0,0.55)",
  },
  confirmTitle: { fontSize: 15, fontWeight: 700, marginBottom: 6 },
  confirmStats: { fontSize: 12, color: "#94A3B8", marginBottom: 12 },
  confirmBtns: { display: "flex", gap: 8, justifyContent: "flex-end" },
  confirmNo: {
    background: "transparent",
    color: "#CBD5E1",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    cursor: "pointer",
  },
  confirmYesEnd: {
    background: "linear-gradient(135deg, #DC2626, #EF4444)",
    color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  confirmYesCancel: {
    background: "linear-gradient(135deg, #475569, #64748B)",
    color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
};

// v2.18.4a — Styles pour le mode pill compact + chevron.
const S = {
  pill: {
    display: "flex", alignItems: "center", gap: 8,
    width: "100%",
    padding: "9px 14px",
    background: "rgba(15, 23, 42, 0.92)",
    border: "1px solid rgba(245, 158, 11, 0.45)",
    borderRadius: 999,
    color: "#F1F5F9",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    backdropFilter: "blur(14px)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.45)",
    fontFamily: "Inter, system-ui, sans-serif",
    justifyContent: "center",
  },
  liveDot: {
    width: 8, height: 8, borderRadius: "50%", background: "#EF4444",
    boxShadow: "0 0 6px rgba(239,68,68,0.9)",
    flexShrink: 0,
  },
  chevronBtn: {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#94A3B8",
    width: 22, height: 22, borderRadius: "50%",
    fontSize: 12, lineHeight: 1, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    marginLeft: "auto",
  },
};
